import React, { useState } from "react";
import { CommentType, ReplyContentType } from "./CommentTypes";
import { Comment } from "./Comment";
import SkeletonComment from "./SkeletonComment";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { CommentsBlockContainer } from "./CommentsBlock.styles";
import { PostComponentOptionsType } from "../Post/Post.types";
import CommentOptionsModal from "../Modals/ServiceModals/CommentOptionsModal/CommentOptionsModal";
import { CommentOptionsModalType } from "../Modals/ServiceModals/ServiceModal.types";
import { SHOW_AVATAR_MODAL_EVENT } from "../Modals/ServiceModals/AvatarModal";
import { UserType } from "@shared/types";
import { SERVICE_MODALS_OPTIONS_STATES } from "../Modals/ServiceFunctionsModal.types";
import {
    fetcherReactToComment,
    fetcherUnReactToComment,
} from "../../DataFetchers/CommentDataFetcher";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import {
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_PROFILE_SUSPENDED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import {
    ErrorState,
    useErrorStore,
} from "../Modals/ServiceModals/GeneralErrorsModal/GlobalState";
import { AxiosError } from "axios";
import { PostCommentsInfo } from "@infrastructure/Services/PostCommentsService.types";
import {
    ReactionActionState,
    SWITCHING_STATE_TO_IDLE_TIME,
    useReactionActionStore,
} from "../ReactionsTooltip/ReactionActionState";
import { Box } from "@mui/material";
import { CommentFormModeEnum } from "../Modals/CommentFormModal/CommentFormModalOpenState";
import { publish } from "@infrastructure/Events/EventBus";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { CommentsFallbackOwnPost } from "../Post/CommentsFallbackOwnPost";
import {
    deleteUserReactionOnObject,
    addUserReactionToObject,
} from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import { CommentsFallback } from "../Post/CommentsFallback";
import { GeneratedComments } from "./GeneratedComments/GeneratedComments";
import { usePostDetailsModalStore } from "../Modals/PostDetailsModal/PostDetailsModalStore";
import { useFunFactsStore } from "../GameTimelinePost/FunFacts/GamesFinFactsStore";
import { FunFactsSection } from "../GameTimelinePost/FunFacts/FunFactsSection";

dayjs.locale("de");

export default function CommentsBlock({
    comments,
    setReplyComment,
    post,
    onEdit,
    onDeleteSuspendSuccess,
    updateComments,
    onCommentButtonClick,
    isCommentsLoading,
    pinnedCommentId,
    onRepliedCommentClick,
}: {
    comments: CommentType[];
    isCommentsLoading: boolean;
    onCommentButtonClick: (modalMode?: CommentFormModeEnum) => void;
    onDeleteSuspendSuccess: (response: PostCommentsInfo) => void;
    onEdit: (comment: CommentType) => void;
    onRepliedCommentClick: (id: number) => void;
    post: PostComponentOptionsType;
    setReplyComment: (item: ReplyContentType) => void;
    updateComments: (newComments: CommentType) => void;
    pinnedCommentId?: number;
}) {
    const { selectedPostId } = usePostDetailsModalStore();
    const { setErrorState } = useErrorStore();

    function handleEditingComment(comment: CommentType) {
        onEdit(comment);
        setOpenCommentOptionsModal(false);
        onCommentButtonClick();
    }

    function handleReplyComment(comment: CommentType) {
        const replyObjectForCurrentComment: ReplyContentType = {
            id: comment.commentContent.id,
            text: comment.commentContent.text,
            images: comment.commentContent.images,
            user: comment.commentContent.user,
        };
        setReplyComment(replyObjectForCurrentComment);
        setOpenCommentOptionsModal(false);
        onCommentButtonClick();
    }

    const [selectedComment, setSelectedComment] = useState({} as CommentType);
    const [openCommentOptionsModal, setOpenCommentOptionsModal] =
        useState(false);
    const [modalCurrentAction, setModalCurrentAction] = useState(
        SERVICE_MODALS_OPTIONS_STATES.COMMENT_STARTING_OPTIONS,
    );

    const [actionInProgress, setActionInProgress] = useState<any>();
    const { setUserAuthContextState } = useUserStore();
    const { setReactionActionState } = useReactionActionStore();
    const createReactOnCommentFunction =
        (commentId: string) => (reactionTypeName: string, action: string) => {
            resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.REACT_ON_COMMENT);
            setReactionActionState(ReactionActionState.LOADING);
            setActionInProgress({
                reactionTypeName: reactionTypeName,
                action: action,
                commentId: commentId,
            });
            setUserAuthContextState(UserLoginActionState.CREATE_REACTION);
            performReactionOnComment(reactionTypeName, action, commentId);
        };

    const performReactionOnComment = (
        reactionTypeName: string,
        action: string,
        commentId: string,
    ) => {
        const updatedComments = JSON.parse(JSON.stringify(comments));
        const commentIndex = updatedComments.findIndex(
            (item: CommentType) =>
                item.commentContent.id.toString() === commentId.toString(),
        );

        if (commentIndex === -1) {
            return;
        }

        const catchError = (err: AxiosError) => {
            switch (err.response?.status) {
                case RESPONSE_CODE_RESOURCE_SUSPENDED:
                case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                    setErrorState(ErrorState.COMMENT_SUSPENDED);
                    break;
                case RESPONSE_CODE_PROFILE_SUSPENDED:
                    setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                    break;
                case RESPONSE_CODE_LIMIT_EXCEEDED:
                    setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                    break;
            }
            setTimeout(
                () => setReactionActionState(ReactionActionState.IDLE),
                SWITCHING_STATE_TO_IDLE_TIME,
            );
        };

        switch (action) {
            case "create":
                fetcherReactToComment(commentId, reactionTypeName)
                    .then((res) => {
                        updatedComments[commentIndex].commentReactions =
                            res.reactions;
                        updateComments(updatedComments[commentIndex]);
                        addUserReactionToObject(
                            commentId,
                            reactionTypeName,
                            ReactionableObjectEnum.COMMENT,
                        );
                        setActionInProgress(undefined);
                        setTimeout(
                            () =>
                                setReactionActionState(
                                    ReactionActionState.IDLE,
                                ),
                            SWITCHING_STATE_TO_IDLE_TIME,
                        );
                    })
                    .catch(catchError);
                break;
            case "delete":
                fetcherUnReactToComment(commentId, reactionTypeName)
                    .then((res) => {
                        updatedComments[commentIndex].commentReactions =
                            res.reactions;
                        updateComments(updatedComments[commentIndex]);
                        deleteUserReactionOnObject(
                            commentId,
                            ReactionableObjectEnum.COMMENT,
                        );
                        setActionInProgress(undefined);
                        setTimeout(
                            () =>
                                setReactionActionState(
                                    ReactionActionState.IDLE,
                                ),
                            SWITCHING_STATE_TO_IDLE_TIME,
                        );
                    })
                    .catch(catchError);
                break;
        }
    };

    const cancelActionInProgress = () => {
        setActionInProgress(undefined);
    };

    useInterruptableAction(
        actionInProgress,
        () =>
            performReactionOnComment(
                actionInProgress.reactionTypeName,
                actionInProgress.action,
                actionInProgress.commentId,
            ),
        cancelActionInProgress,
    );

    const commentOptionsModalProps: CommentOptionsModalType = {
        open: openCommentOptionsModal,
        setOpen: setOpenCommentOptionsModal,
        selectedComment: selectedComment,
        onCommentReplyOptionClickHandler: handleReplyComment,
        handleEditingComment: handleEditingComment,
        onCommentDeleteSuspendSuccess: onDeleteSuspendSuccess,
        modalCurrentAction: modalCurrentAction,
        setModalCurrentAction: setModalCurrentAction,
        createReactOnCommentFunction: createReactOnCommentFunction,
        isLatestComment: selectedComment.id === post.data?.latestComment?.id,
    };

    function handleSelectingComment(comment: CommentType) {
        setSelectedComment(comment);
        setModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.COMMENT_STARTING_OPTIONS,
        );
        setOpenCommentOptionsModal(true);
    }

    function handleReportingComment(comment: CommentType) {
        setSelectedComment(comment);
        setModalCurrentAction(SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT);
        setOpenCommentOptionsModal(true);
    }

    function handleSelectingCommentUser(user: UserType) {
        publish({ name: SHOW_AVATAR_MODAL_EVENT, user: user });
    }

    const isOwnPost = useIsCurrentUserId(post?.data?.user?.uuid);

    const { funFacts: funFactsList } = useFunFactsStore();
    const funFacts = funFactsList?.[post.id];

    return (
        <CommentsBlockContainer>
            {selectedComment?.commentContent != null && (
                <CommentOptionsModal {...commentOptionsModalProps} />
            )}
            <GeneratedComments postId={selectedPostId} />
            <FunFactsSection funFacts={funFacts} />
            {comments &&
                comments.map((currentValue: CommentType, index: number) => {
                    const commentOpts = {
                        comment: currentValue,
                        showCommentAvatarOptionsModal:
                            handleSelectingCommentUser,
                        handleReplyComment,
                        handleSelectingComment,
                        createReactOnCommentFunction,
                        handleReportingComment,
                        onRepliedCommentClick,
                    };
                    return (
                        <Box key={index} marginBottom={"8px"}>
                            <Comment {...commentOpts} />
                        </Box>
                    );
                }, {})}
            {isCommentsLoading ? (
                <SkeletonComment />
            ) : !pinnedCommentId /*&& !funFacts?.length*/ ? (
                comments &&
                !comments.length &&
                post.data &&
                (isOwnPost ? (
                    <CommentsFallbackOwnPost />
                ) : (
                    <CommentsFallback
                        onCommentButtonClick={() =>
                            onCommentButtonClick(CommentFormModeEnum.TEXT)
                        }
                    />
                ))
            ) : (
                <></>
            )}
        </CommentsBlockContainer>
    );
}
